import { AxiosRequestConfig, AxiosResponse } from 'axios';
import { appSyncItemNames, constants } from '../constants/constants';
import moment from 'moment';
import * as types from './actionTypes';
import { msalFetch } from '../components/auth/Auth-Utils';
import API from '../constants/apiEndpoints';
import {
    IinstallBasePart,
    IinstallBasePartPopulated,
    Ioption,
    Ipart,
    IquotePartCart,
    ThunkResult,
    IquotePartPopulated,
    IshoppingCartItem
} from '../models';
import { beginAjaxCall } from './ajaxStatusActions';
import orderBy from 'lodash/orderBy';
import { initialCartReducer, initialInstallBasePart, initialQuotePart } from '../reducers/initialState';
import { getSelectedJobID } from '../reducers/commonSelectors';
import { AppSyncItemStatus, partTypeEnum } from '../models-enums';

import {
    quotePartsToCartItems,
    selectQuoteCartForJob,
    shoppingCartItemsToQuoteParts
} from '../components/parts/PartUtilities';

import { v4 as uuidv4 } from 'uuid';
import { getAllPartsFromStore } from '../store/configureStore';

/**
 * find the existing cart if there is one, then update the message and items
 * @param message
 * @param items
 * @returns
 */
export function saveQuoteCart(message: string, items: IshoppingCartItem[]): ThunkResult<any> {
    return (dispatch, getState) => {
        const foundCart = selectQuoteCartForJob(getState());
        const selectedJobID = getSelectedJobID(getState(), {});
        let originalCart = { ...foundCart };

        if (foundCart.id.length === 0) {
            originalCart = { ...originalCart, isDeleted: true }; // rollback by deleting it
        }

        const updatedCart: IquotePartCart = {
            ...foundCart,
            id: foundCart.id || uuidv4(),
            quoteItems: shoppingCartItemsToQuoteParts(items, foundCart.quoteItems),
            jobID: foundCart.jobID || selectedJobID,
            message
        };
        const updatedCartForApi = {
            ...updatedCart,
            quoteItems: updatedCart.quoteItems.map((item: any) => {
                let { ...itemWithoutPart } = item;
                if (typeof itemWithoutPart.bundle !== 'string') {
                    itemWithoutPart.bundle = itemWithoutPart.bundle.toString();
                }
                return itemWithoutPart;
            })
        };
        const url = API.job.saveQuoteCart;
        const axiosOptions: AxiosRequestConfig = {
            method: 'post',
            data: updatedCartForApi,
            url
        };
        dispatch({
            type: types.CHECKOUT_QUOTE_PARTS_SUCCESS,
            cart: updatedCart,
            meta: {
                offline: {
                    effect: {
                        axiosOptions,
                        message: 'save cart'
                    },
                    rollback: {
                        type: types.CHECKOUT_QUOTE_PARTS_FAILED,
                        cart: originalCart
                    }
                }
            }
        });
    };
}

/**
 *
 * @param parts
 * @returns partOptions sorted by the part description
 */
export const partsToOptions = (parts: unknown): Ioption[] => {
    if (Array.isArray(parts)) {
        const partOptions = parts.map((item: Ipart) => {
            return {
                value: item.id,
                label: `${item.number} - ${item.description}`,
                order: item.description
            };
        });
        return orderBy(partOptions, 'order');
    } else {
        return [];
    }
};

export const searchPartsForAsyncSelect = (
    search: string,
    type: partTypeEnum
): Promise<{ partOptions: Ioption[]; parts: Ipart[]; networkError: boolean }> => {
    const url = API.part.search;
    const axiosOptions: AxiosRequestConfig = {
        method: 'get',
        params: { number: search, type }
    };

    return msalFetch(url, axiosOptions)
        .then(data => {
            if (!data.data) {
                throw new Error('missing data');
            } else {
                return {
                    partOptions: partsToOptions(data.data.result),
                    parts: data.data.result,
                    networkError: false
                };
            }
        })
        .catch((error: any) => {
            constants.handleError(error, 'searchPartsForAsyncSelect', 'error', false);
            console.error('[searchPartsForAsyncSelect]:', error);
            return { partOptions: [], parts: [], networkError: true };
        });
};

export function saveInstallBasePart(
    installBasePart: IinstallBasePart,
    originalInstallBasePart: IinstallBasePartPopulated | IinstallBasePart,
    installBaseIDOverride?: string
): ThunkResult<any> {
    return (dispatch, getState) => {
        if ('part' in originalInstallBasePart) {
            const {
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                part,
                ...cleanedOriginalInstallBasePart
            } = originalInstallBasePart;
            originalInstallBasePart = cleanedOriginalInstallBasePart;
        }
        if ('part' in installBasePart) {
            const {
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                part,
                ...cleanedInstallBasePart
            } = installBasePart;
            installBasePart = cleanedInstallBasePart;
        }
        if (!installBasePart.id || installBasePart.id.length === 0) {
            installBasePart = { ...installBasePart, id: uuidv4() };
            originalInstallBasePart = { ...installBasePart, isDeleted: true };
        }

        // If the part came from a virtual IB, then we need to override the installBaseID, otherwise the part will be saved to the wrong IB
        if (installBaseIDOverride) {
            installBasePart.installBaseID = installBaseIDOverride;
        }

        const url = API.part.saveInstallBasePart;
        const axiosOptions: AxiosRequestConfig = {
            method: 'post',
            data: [installBasePart],
            url
        };
        dispatch({
            type: types.SAVE_INSTALL_BASE_PART,
            installBasePart,
            meta: {
                offline: {
                    effect: {
                        axiosOptions,
                        message: 'save install base part'
                    },
                    rollback: {
                        type: types.SAVE_INSTALL_BASE_PART,
                        installBasePart: originalInstallBasePart
                    }
                }
            }
        });
    };
}
export const addPart = (part: Ipart) => ({
    type: types.ADD_PART,
    part
});

export function savePartAsInstallBasePart(part: Ipart, installBaseID: string, quantity = 1): ThunkResult<any> {
    return (dispatch, getState) => {
        const { selectedJob } = getState().manageJob;

        const installBasePart: IinstallBasePartPopulated = {
            ...initialInstallBasePart,
            jobID: selectedJob.id,
            installBaseID,
            directMaterialID: part.id,
            directPartQuantity: quantity,
            part
        };
        dispatch(addPart(part));
        dispatch(saveInstallBasePart(installBasePart, initialInstallBasePart));
    };
}

export function getAllInstallBasePartsForFacility(facilityId: string): ThunkResult<any> {
    return (dispatch, getState) => {
        dispatch(beginAjaxCall());
        const axiosOptions: AxiosRequestConfig = {
            method: 'get',
            params: { facilityId }
        };

        const url = API.part.GetAllInstallBasePartsForFacility;

        return msalFetch(url, axiosOptions)
            .then((data: AxiosResponse<any>) => {
                if (!data.data) {
                    throw new Error('error getting all parts users');
                } else {
                    dispatch({
                        type: types.GET_ALL_INSTALL_BASE_PARTS_SUCCESS,
                        payload: data.data,
                        updateDate: moment().unix()
                    });
                    return data;
                }
            })
            .catch(error => {
                dispatch({ type: types.GET_ALL_INSTALL_BASE_PARTS_FAILED });
                constants.handleError(error, 'get all parts');
                console.error('[getAllInstallBasePartsForFacility]:', error);
                throw error;
            });
    };
}

export function getAllParts(skipQueue = false): ThunkResult<any> {
    return async (dispatch, getState) => {
        const url = API.part.getAllParts;
        const axiosOptions: AxiosRequestConfig = {
            method: 'get',
            params: { includeDeleted: true },
            url
        };

        if (skipQueue) {
            dispatch({
                type: types.UPDATE_INITIAL_APP_SYNC_ITEM_STATUS,
                name: appSyncItemNames.Parts,
                status: AppSyncItemStatus.inProgress
            });

            try {
                // Load parts from IndexedDB (stored when products load from /material/stream)
                const partsFromStore = await getAllPartsFromStore();
                const parts = Object.values(partsFromStore);

                // Get relationship data from /part/getAllParts endpoint
                const relationshipsResp = await msalFetch(url, axiosOptions);
                const relationshipData = relationshipsResp.data;

                // Combine parts from IndexedDB with relationship data from API
                const payload = {
                    data: {
                        parts,
                        jobParts: relationshipData.jobParts || [],
                        stockParts: relationshipData.stockParts || [],
                        workOrderParts: relationshipData.workOrderParts || [],
                        suggestedParts: relationshipData.suggestedParts || [],
                        installBaseParts: relationshipData.installBaseParts || [],
                        carts: relationshipData.carts || []
                    }
                };

                // Dispatch the success action
                // skipAsyncProcessing flag tells middleware these parts are already clean from IndexedDB
                dispatch({
                    type: types.GET_ALL_PARTS_SUCCESS,
                    payload: {
                        ...payload,
                        skipAsyncProcessing: true // Parts from IndexedDB are already in correct format
                    },
                    updateDate: moment().unix()
                });

                // Report actual record count for parts
                const partsCount = parts.length;

                dispatch({
                    type: types.UPDATE_SYNC_ITEM_RECORD_COUNT,
                    name: appSyncItemNames.Parts,
                    recordCount: partsCount
                });

                // Report completion and remove sync item (no async processing for IndexedDB parts)
                dispatch({
                    type: types.UPDATE_SYNC_ITEM_PROGRESS,
                    name: appSyncItemNames.Parts,
                    recordsProcessed: partsCount,
                    recordCount: partsCount
                });
                dispatch({ 
                    type: types.REMOVE_INITIAL_APP_SYNC_ITEM, 
                    name: appSyncItemNames.Parts 
                });
            } catch (error) {
                constants.handleError(error, 'get all parts');
                console.error('[getAllParts]:', error);

                dispatch({
                    type: types.UPDATE_INITIAL_APP_SYNC_ITEM_STATUS,
                    name: appSyncItemNames.Parts,
                    status: AppSyncItemStatus.failed
                });
                
                throw error; // Re-throw to propagate failure to Promise chain
            }
        } else {
            dispatch({
                type: types.GET_ALL_PARTS,
                meta: {
                    offline: {
                        effect: { axiosOptions, message: 'Get All Parts' },
                        commit: { type: types.GET_ALL_PARTS_SUCCESS, updateDate: moment().unix() }
                    }
                }
            });
        }
    };
}

export function deleteInstallBasePart(installBasePartID: string): ThunkResult<any> {
    return (dispatch, getState) => {
        const installBasePartsByID = getState().parts.installBasePartsByID;
        const originalInstallBasePart = installBasePartsByID[installBasePartID];

        if (!originalInstallBasePart) {
            console.error('Install base part not found:', installBasePartID);
            return;
        }

        const deletedInstallBasePart: IinstallBasePart = {
            ...originalInstallBasePart,
            isDeleted: true
        };
        dispatch(saveInstallBasePart(deletedInstallBasePart, originalInstallBasePart));
    };
}

export function ensureJobWorkOrderParts(jobID: string, skipQueue = false): ThunkResult<any> {
    return (dispatch, getState) => {
        const url = API.part.ensureJobWorkOrderParts;
        const axiosOptions: AxiosRequestConfig = {
            method: 'get',
            params: { jobID },
            url
        };

        if (skipQueue) {
            dispatch({
                type: types.UPDATE_INITIAL_APP_SYNC_ITEM_STATUS,
                name: appSyncItemNames.JobWorkOrderParts,
                status: AppSyncItemStatus.inProgress
            });

            return msalFetch(url, axiosOptions)
                .then((data: AxiosResponse<any>) => {
                    dispatch({ type: types.REMOVE_INITIAL_APP_SYNC_ITEM, name: appSyncItemNames.JobWorkOrderParts });

                    dispatch({
                        type: types.ENSURE_JOB_WORKORDER_PARTS_SUCCESS,
                        payload: data.data,
                        jobID,
                        updateDate: moment().unix()
                    });
                    return data;
                })
                .catch(error => {
                    constants.handleError(error, 'ensure job work order parts');
                    console.error('[ensureJobWorkOrderParts]:', error);
                    dispatch({
                        type: types.UPDATE_INITIAL_APP_SYNC_ITEM_STATUS,
                        name: appSyncItemNames.JobWorkOrderParts,
                        status: AppSyncItemStatus.failed
                    });
                    throw error;
                });
        } else {
            dispatch({
                type: types.ENSURE_JOB_WORKORDER_PARTS,
                meta: {
                    offline: {
                        effect: { axiosOptions, message: 'Ensure Job Work Order Parts' },
                        commit: {
                            type: types.ENSURE_JOB_WORKORDER_PARTS_SUCCESS,
                            jobID,
                            updateDate: moment().unix()
                        }
                    }
                }
            });
        }
    };
}

export function addPartToCart(part: Ipart, installBaseID?: string, bundle?: number): ThunkResult<any> {
    return (dispatch, getState) => {
        let quantity = 1;

        if (part.quantity !== undefined) {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            quantity = part.quantity!;
        }

        const item: IquotePartPopulated = {
            ...initialQuotePart,
            materialID: part.id,
            id: uuidv4(),
            part,
            quantity,
            installBaseID: installBaseID || '',
            bundle: bundle || 1
        };

        dispatch({
            type: types.ADD_TO_CART_QUOTE_PARTS,
            item,
            jobID: getState().manageJob.selectedJob.id
        });
        dispatch(addPart(part));
    };
}

export function deleteQuoteItemFromQuoteCart(itemID: string): ThunkResult<any> {
    return (dispatch, getState) => {
        const quoteCart = selectQuoteCartForJob(getState());
        const quoteItemToDelete = quoteCart.quoteItems.find(x => x.id === itemID);
        dispatch({
            type: types.DELETE_QUOTE_ITEM_FROM_QUOTE_CART,
            item: quoteItemToDelete,
            quoteCartId: quoteCart.id
        });
    };
}

/**
 * take the cart for this job (create one if needed), update the items to be shoppingCart items
 * set this cart as the selectedCart
 * @returns
 */
export function setSelectedCart(): ThunkResult<any> {
    return (dispatch, getState) => {
        const foundCart = selectQuoteCartForJob(getState());

        if (foundCart) {
            const existingCartItems = quotePartsToCartItems(foundCart.quoteItems);
            const existingCart = {
                addedIDs: Object.keys(existingCartItems),
                message: foundCart.message,
                itemsByID: existingCartItems,
                quantity: existingCartItems.quantity
            };
            dispatch({
                type: types.SET_CART_QUOTE_PARTS,
                cart: existingCart
            });
        } else {
            const newCart = {
                ...initialCartReducer
            };
            dispatch({
                type: types.SET_CART_QUOTE_PARTS,
                cart: newCart
            });
        }
    };
}
export const setSelectedInstallBasePartID = (id: string) => ({
    type: types.SET_SELECTED_INSTALL_BASE_PART_ID,
    id
});

export const clearSelectedInstallBasePartID = () => ({
    type: types.CLEAR_SELECTED_INSTALL_BASE_PART_ID
});

export const setPartFormValues = (formValues: { [key: string]: any }) => ({
    type: types.SET_FORM_VALUES_PART,
    formValues
});

export const updatePartFormValue = (formValues: { [key: string]: any }) => ({
    type: types.UPDATE_FORM_VALUES_PART,
    formValues
});

/*
// REMOVED: processPartsNonBlocking - replaced with async reducer processing
export function processPartsNonBlocking(payload: any, updateDate: number): ThunkResult<any> {
    return (dispatch, getState) => {
        let parts = payload.data?.parts || payload.data || [];

        if (!Array.isArray(parts) || parts.length === 0) {
            // No parts to process, complete immediately
            dispatch({ type: types.REMOVE_INITIAL_APP_SYNC_ITEM, name: appSyncItemNames.Parts });
            return Promise.resolve();
        }

        const chunkSize = getOptimalChunkSize(parts.length);
        let processedCount = 0;

        // Start performance tracking
        perfTracker.startSession(`PARTS_PROCESSING_${parts.length}_items`);
        perfTracker.addEvent('PARTS_CHUNKING_START', 0, parts.length);

        // Process in chunks and dispatch incremental updates to avoid blocking reducers
        return processDataInChunks({
            data: parts,
            chunkSize,
            processItem: part => part, // Just pass through without cleaning - let reducer handle it
            onProgress: (processed, total) => {
                perfTracker.addEvent('PARTS_CHUNK_PROCESSED', processed, total);
                // Dispatch progress update to keep UI responsive
                dispatch({
                    type: types.GET_ALL_PARTS_PROCESSING_PROGRESS,
                    processedParts: [], // Don't send parts here, just progress
                    processed,
                    total
                });
            }
        })
            .then(processedParts => {
                perfTracker.addEvent('PARTS_CHUNKING_COMPLETE', processedParts.length, processedParts.length);

                // Process in smaller chunks to avoid blocking the reducers
                const reducerChunkSize = Math.max(1, Math.floor(chunkSize / 4)); // Smaller chunks for reducer processing
                perfTracker.addEvent('PARTS_REDUCER_DISPATCH_START', 0, processedParts.length);

                return new Promise<any[]>(resolve => {
                    let currentIndex = 0;
                    let dispatchCount = 0;

                    function processNextReducerChunk() {
                        const endIndex = Math.min(currentIndex + reducerChunkSize, processedParts.length);
                        const chunk = processedParts.slice(currentIndex, endIndex);

                        if (chunk.length > 0) {
                            perfTracker.addEvent(
                                `PARTS_REDUCER_CHUNK_${dispatchCount}`,
                                currentIndex,
                                processedParts.length
                            );

                            // Dispatch chunk to reducers
                            dispatch({
                                type: types.GET_ALL_PARTS_SUCCESS,
                                payload: {
                                    ...payload,
                                    data: {
                                        ...payload.data,
                                        parts: chunk
                                    }
                                },
                                updateDate,
                                isIncremental: true // Flag to indicate this is an incremental update
                            });

                            perfTracker.addEvent(
                                `PARTS_REDUCER_DISPATCH_${dispatchCount}`,
                                currentIndex + chunk.length,
                                processedParts.length
                            );
                            dispatchCount++;
                        }

                        currentIndex = endIndex;

                        if (currentIndex >= processedParts.length) {
                            perfTracker.addEvent(
                                'PARTS_REDUCER_DISPATCH_COMPLETE',
                                processedParts.length,
                                processedParts.length
                            );

                            // All chunks processed, remove from sync queue
                            dispatch({
                                type: types.REMOVE_INITIAL_APP_SYNC_ITEM,
                                name: appSyncItemNames.Parts
                            });

                            perfTracker.endSession();
                            resolve(processedParts);
                        } else {
                            // Yield to UI thread before processing next chunk
                            setTimeout(processNextReducerChunk, 10);
                        }
                    }

                    // Start processing chunks
                    processNextReducerChunk();
                });
            })
            .catch(error => {
                console.error('[processPartsNonBlocking]:', error);

                // Fall back to regular processing on error
                dispatch({
                    type: types.GET_ALL_PARTS_SUCCESS,
                    payload,
                    updateDate
                });

                dispatch({ type: types.REMOVE_INITIAL_APP_SYNC_ITEM, name: appSyncItemNames.Parts });
            });
    };
}
*/
